<script setup>


// Props
const props = defineProps({
  video: {
    type: [Object, Array],
    default: () => ({})
  },
  controls: {
    type: Boolean,
    default: false
  },
  darkControls: {
    type: Boolean,
    default: true
  }
})

const pause = ref(false)
const videoRef = ref(null)

// Methods
const onPlay = () => {
  videoRef.value?.play()
  pause.value = false
}

const onPause = () => {
  videoRef.value?.pause()
  pause.value = true
}

// Computed
const normalizedVideo = computed(() => {
  if (Array.isArray(props.video)) {
    return props.video[0] || {}
  }
  return props.video || {}
})

const videoType = computed(() => {
  const url = normalizedVideo.value?.url || ''
  if (!url) return 'video/mp4'
  
  // Determine MIME type based on file extension
  if (url.match(/\.webm$/i)) {
    return 'video/webm'
  }
  if (url.match(/\.mp4$/i)) {
    return 'video/mp4'
  }
  if (url.match(/\.ogv$/i)) {
    return 'video/ogg'
  }
  
  // Default to mp4 if extension is unknown
  return 'video/mp4'
})

const videoAriaLabel = computed(() => {
  const candidate = (
    normalizedVideo.value?.title ||
    normalizedVideo.value?.label ||
    normalizedVideo.value?.alt ||
    ''
  ).toString().trim()

  return candidate || 'Background video'
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div class="video">
  <video v-if="normalizedVideo?.url?.length"
    preload="auto"
    ref="videoRef"
    autoplay="autoplay"
    :key="normalizedVideo.url"
    loop="loop"
    muted="muted"
    playsinline=""
    :aria-label="videoAriaLabel"
    style="object-fit: cover; object-position: 50% 50%;">
    <source :src="normalizedVideo.url" :type="videoType">
  </video>
  
  <div v-if="controls" class="video-controls">
    <button
      v-if="!pause"
      type="button"
      title="Pause"
      aria-label="Pause video"
      @click="onPause"
      class="circle-blur-wrapper">
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
        <circle cx="13" cy="13" r="13" :fill="darkControls ? 'black' : 'white'" fill-opacity="0.4" />
        <rect x="9" y="8" width="3" height="10" rx="1" fill="white"/>
        <rect x="14" y="8" width="3" height="10" rx="1" fill="white"/>
      </svg>
    </button>

    <button
      v-else
      type="button"
      title="Play"
      aria-label="Play video"
      @click="onPlay"
      class="circle-blur-wrapper">
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
        <circle cx="13" cy="13" r="13" :fill="darkControls ? 'black' : 'white'" fill-opacity="0.4" />
        <path d="M18.5 12.134C19.1667 12.5189 19.1667 13.4811 18.5 13.866L11 18.1962C10.3333 18.5811 9.5 18.0999 9.5 17.3301L9.5 8.66987C9.5 7.90007 10.3333 7.41895 11 7.80385L18.5 12.134Z" fill="white"/>
      </svg>
    </button>
  </div>
</div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.video {
  display: flex;
  overflow: hidden;
  border-radius: 8px;
  position: relative;
}

video {
  width: 100%;
}

.circle-blur-wrapper {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	backdrop-filter: blur(8px);
	-webkit-backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	position: absolute;
	right: 16px;
	bottom: 16px;
	cursor: pointer;
}

@media (min-width: 768px) {
  .video {
    border-radius: 16px;
  }

  .circle-blur-wrapper {
    right: 24px;
		bottom: 24px;
  }
}



</style>